import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { Input, Tab, TabGroup } from '@carbonify/ui';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { createResolver, type SchemaFields, SearchField } from 'constants/validation';
import { useRouter } from 'next/router';
import useConfig from 'services/config';
import { clearUndefinedValues } from 'utils/object';

import styles from './SearchForm.module.css';

interface Props {
  searchPlaceholder: string;
}

export default function SearchForm({ searchPlaceholder }: Props) {
  const { categories } = useConfig();
  const { query, push } = useRouter();
  const methods = useForm<SchemaFields<typeof SearchField>>({
    resolver: createResolver(SearchField),
    defaultValues: query,
    mode: 'all',
    criteriaMode: 'all',
  });

  // eslint-disable-next-line react-hooks/incompatible-library
  const values = methods.watch();

  const onSubmit: SubmitHandler<SchemaFields<typeof SearchField>> = async ({ search, category }) => {
    push(
      {
        query: clearUndefinedValues({
          ...query,
          search: search || undefined,
          category: category || undefined,
          page: 1,
        }),
      },
      undefined,
      {
        shallow: true,
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={methods.handleSubmit(onSubmit)}
        className={styles.form}
      >
        <Input
          type="search"
          placeholder={searchPlaceholder}
          suffix={<MagnifyingGlassIcon className={styles.icon} />}
          className={styles.input}
          enterKeyHint="search"
          {...methods.register('search')}
        />
        {categories && categories.length > 1 ? (
          <TabGroup showAsButtonsOnMobile>
            <Tab
              active={!values.category}
              onClick={() => {
                methods.setValue('category', '', { shouldValidate: true });
                onSubmit({ ...values, category: '' });
              }}
            >
              Alle
            </Tab>
            {categories?.map((category) => (
              <Tab
                key={category}
                active={category === values.category}
                onClick={() => {
                  methods.setValue('category', category, { shouldValidate: true });
                  onSubmit({ ...values, category });
                }}
              >
                {category}
              </Tab>
            ))}
          </TabGroup>
        ) : null}
      </form>
    </FormProvider>
  );
}
